import { createContext, type PropsWithChildren, useState } from "react";
import Button from "@/components/ui/button.tsx";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog.tsx";
import useConfigCode from "@/features/sub-families/hooks/use-config-code.ts";
import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import {
    type ChangeValue,
    Difference,
    type Differences,
    type Product,
    type ProductPropertyKey,
    type ProductReference,
    type Property,
    type PropertyConfigCode,
    type SmartNumberMapping,
} from "@/features/sub-families/types";
import {
    canUpdateWithoutCheckingCombinations,
    findBestMatchingProduct,
    formatDifference,
    getDifferences,
    getProperty,
} from "@/features/sub-families/utils/change-filter-option.ts";
import { formatFilterLabel } from "@/features/sub-families/utils/formatters.ts";
import {
    getDimTypePowerSupplyResetMappingByProductScenario,
    getPowerSupplyOptionsByProductScenario,
    getProductScenario,
} from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import {
    getEmptyConfigCode,
    getSmartNumberDescriptorByProductProperty,
} from "@/features/sub-families/utils/smart-number.ts";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import useInitializedContext from "@/hooks/use-initialized-context.ts";
import { productsSetCurrentProductReferenceAction } from "@/redux/slices/products-slice.ts";
import {
    smartNumberChangeMappingAction,
    smartNumberChangeMultipleMappingAction,
} from "@/redux/slices/smart-number-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import { type Nullable, SmartNumberDescriptor, type SmartNumberDescriptorKey } from "@/types";

// region Context
type ChangeFilterOptionContextType = {
    changeFilterOption: (descriptor: SmartNumberDescriptorKey, configCode: PropertyConfigCode) => void;
    checkAndWarnAboutConfigurationChanges: (change: ChangeValue) => void;
};

const ChangeFilterOptionContext = createContext<Nullable<ChangeFilterOptionContextType>>(null);

export const useChangeFilterOption = () => {
    return useInitializedContext(ChangeFilterOptionContext, "ChangeFilterOptionProvider");
};
// endregion

export default function ChangeFilterOptionProvider({ children }: PropsWithChildren) {
    const currentProduct = useCurrentProduct();
    const t = useTranslations();

    const { getConfigCodeByProperty } = useConfigCode();

    const dispatch = useAppDispatch();
    const products = useAppSelector((state) => state.products.items);
    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);
    const smartNumberDescription = useAppSelector((state) => state.smartNumber.description);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [differences, setDifferences] = useState<Differences>([]);
    const [newProductReference, setNewProductReference] = useState<Nullable<ProductReference>>(null);

    const product = newProductReference ? products[newProductReference] : null;

    const changeProduct = (reference: ProductReference, differencesWithRemovals: Differences) => {
        const REMOVAL_PROPERTIES: ProductPropertyKey[] = ["ceiling_meta_data", "light_acc"];

        // Dispatch reset for mounting kit, and accessory updates before changing the reference!
        const changes = differencesWithRemovals
            .map<Nullable<ChangeValue>>((diff) => {
                const property = diff.property as ProductPropertyKey;

                const newProductScenario = getProductScenario(product);
                if (newProductScenario === null) return null;

                if (property === "dimming_type") {
                    return getDimTypePowerSupplyResetMappingByProductScenario(
                        newProductScenario,
                        smartNumberDescription,
                    );
                }

                if (!REMOVAL_PROPERTIES.includes(property)) return null;

                const descriptor = getSmartNumberDescriptorByProductProperty(property);
                if (!descriptor) return null;

                const configCode = getEmptyConfigCode(descriptor, smartNumberDescription);
                if (!configCode) return null;

                return { descriptor, configCode };
            })
            .filter((changeValue) => changeValue !== null);

        dispatch(smartNumberChangeMultipleMappingAction(changes));
        dispatch(productsSetCurrentProductReferenceAction(reference));
    };

    const acceptNewProduct = () => {
        if (newProductReference) changeProduct(newProductReference, differences);

        setDialogOpen(false);
        setDifferences([]);
        setNewProductReference(null);
    };

    const checkAndWarnAboutConfigurationChanges = (change: ChangeValue) => {
        if (!smartNumberMapping) return; // This should not happen at this point in time

        const bestMatch = findBestMatchingProduct(currentProduct, change, products, smartNumberMapping);
        if (!bestMatch) return;

        const [bestMatchingProductReference, bestMatchingProduct] = bestMatch;

        const actualDifferences = getDifferences(
            currentProduct,
            bestMatchingProduct,
            smartNumberMapping,
            smartNumberDescription,
            change.descriptor,
        );

        // Silently update the product when no differences were detected or only the massCode changed
        if (actualDifferences.length === 0) {
            changeProduct(bestMatchingProductReference, actualDifferences);
            return;
        }

        setDialogOpen(true);
        setDifferences(actualDifferences);
        setNewProductReference(bestMatchingProductReference);
    };

    const changeFilterOption = (descriptor: SmartNumberDescriptorKey, configCode: PropertyConfigCode) => {
        if (!smartNumberMapping) {
            // Should not happen at this point in time
            return;
        }

        const change = { descriptor, configCode };

        if (canUpdateWithoutCheckingCombinations(currentProduct, descriptor)) {
            dispatch(smartNumberChangeMappingAction(change));
            return;
        }

        checkAndWarnAboutConfigurationChanges(change);
    };

    return (
        <ChangeFilterOptionContext value={{ changeFilterOption, checkAndWarnAboutConfigurationChanges }}>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                {children}
                <DialogContent className="dialog--change-filter-option">
                    <DialogHeader>
                        <DialogTitle>{t("change_features_title")}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <p>{t("change_features_description")}</p>
                        {differences.length > 0 && (
                            <ul>
                                {differences.map((diff) => {
                                    // When a change is detected, we want to get the new values
                                    // Otherwise, we want to get the old values that will be removed
                                    const productToUse =
                                        diff.difference === Difference.Change ? product : currentProduct;

                                    const productProperty = diff.property as ProductPropertyKey;

                                    const configCode = getConfigCodeByProperty(productProperty);
                                    const property = getProperty(productProperty, productToUse, configCode);

                                    if (!property) return null;

                                    const powerSupply =
                                        productProperty === "dimming_type"
                                            ? getCurrentPowerSupply(currentProduct, smartNumberMapping)
                                            : null;

                                    const title = formatDifference(diff.difference, property.name, t);
                                    const message = powerSupply ? powerSupply.label : formatFilterLabel(property as Property); // prettier-ignore

                                    return (
                                        <li key={diff.property}>
                                            <b>{title}: </b>
                                            <span>{message}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button background="dark" onClick={acceptNewProduct}>
                            {t("change_features_accept")}
                        </Button>
                        <DialogClose variant="link">{t("change_features_cancel")}</DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </ChangeFilterOptionContext>
    );
}

function getCurrentPowerSupply(currentProduct: Nullable<Product>, smartNumberMapping: Nullable<SmartNumberMapping>) {
    const productScenario = getProductScenario(currentProduct);
    if (productScenario === null) return null;

    const configCode = smartNumberMapping?.[SmartNumberDescriptor.PowerSupply];

    const supplies = getPowerSupplyOptionsByProductScenario(productScenario, currentProduct);
    return supplies.find((supply) => supply.configCode === configCode) ?? null;
}
